import React, { useMemo, useState } from 'react';

import { Globe } from 'lucide-react';

import AJAX from 'common/AJAX';
import ErrorMessage from 'common/ErrorMessage';
import UserCompanySelect from 'common/subdomain/admin/UserCompanySelect';
import PrioritySelect from 'common/subdomain/admin/v2/AdminV2IdeaSheet/InternalView/Insights/PrioritySelect';
import Button from 'common/uiV3/Button';
import HStack from 'common/uiV3/HStack';
import Input from 'common/uiV3/Input';
import { Separator } from 'common/uiV3/Separator';
import Textarea from 'common/uiV3/Textarea';
import VStack from 'common/uiV3/VStack';
import parseAPIResponse from 'common/util/parseAPIResponse';
import validateInput from 'common/validateInput';

import type { Idea } from 'common/api/endpoints/idea';
import type { Insight } from 'common/api/endpoints/insight';
import type { InsightPriority } from 'common/constants/insights';
import Card from 'common/uiV3/Card';

const getDefaultUser = (insight: Insight | undefined) => {
  const user = insight?.users?.[0];
  if (!user) {
    return undefined;
  }

  return {
    avatar: user.avatarURL,
    label: user.name,
    value: user._id,
  };
};

const getDefaultCompany = (insight: Insight | undefined) => {
  const company = insight?.company;
  if (!company) {
    return undefined;
  }

  return {
    label: company.name,
    value: company.thirdPartyCompanyID,
  };
};

type Props = {
  idea: Idea;
  insight?: Insight;
  onClose: () => void;
  onSuccess: () => Promise<void>;
};

export default function InsightEditor({ idea, insight, onClose, onSuccess }: Props) {
  const defaultUser = getDefaultUser(insight);
  const defaultCompany = getDefaultCompany(insight);

  const [upserting, setUpserting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [insightValue, setInsightValue] = useState<string>(insight?.value ?? '');
  const [priority, setPriority] = useState<InsightPriority | null>(insight?.priority ?? null);
  const [thirdPartyCompanyID, setThirdPartyCompanyID] = useState<string | undefined>(
    defaultCompany?.value || undefined,
  );
  const [url, setUrl] = useState<string>(insight?.url ?? '');
  const [userID, setUserID] = useState<string | undefined>(defaultUser?.value || undefined);

  const onUpsert = async () => {
    if (upserting) {
      return;
    }

    setUpserting(true);
    setError(null);

    const response = await AJAX.post('/api/insights/upsert', {
      ideaID: idea._id,
      priority,
      thirdPartyCompanyID: thirdPartyCompanyID || null,
      url: url.length > 0 ? url : null,
      userIDs: userID ? [userID] : [],
      value: insightValue,
      ...(insight ? { insightID: insight._id } : {}),
    });

    const { error, parsedResponse } = parseAPIResponse<{ idea: Idea }>(response, {
      isSuccessful: (response) => 'insight' in response,
    });

    if (error || !parsedResponse) {
      setError('Something went wrong, please contact our team for support.');
      setUpserting(false);
      return;
    }

    await onSuccess();

    onClose();
    setUpserting(false);
  };

  const canUpsert = useMemo(() => {
    const hasValidInsight = insightValue.length > 0;
    const hasValidURL = url.length === 0 || validateInput.primitives.url(url);
    const hasValidAttribution = userID || thirdPartyCompanyID;
    const hasValidData = hasValidInsight && hasValidURL && hasValidAttribution;

    if (!hasValidData) {
      return false;
    }

    if (!insight) {
      return true;
    }

    const user = insight.users?.[0];
    const company = insight.company;

    const hasDifferentValue = insight.value !== insightValue;
    const hasDifferentURL = insight.url !== url;
    const hasDifferentUser = user?._id !== userID;
    const hasDifferentCompany = company?.thirdPartyCompanyID !== thirdPartyCompanyID;
    const hasDifferentPriority = insight.priority !== priority;
    return (
      hasDifferentValue ||
      hasDifferentURL ||
      hasDifferentUser ||
      hasDifferentCompany ||
      hasDifferentPriority
    );
  }, [insight, insightValue, url, userID, thirdPartyCompanyID, priority]);

  return (
    <Card className="p-0">
      <div className="p-3">
        <Textarea
          autoFocus
          placeholder="Insight"
          rows={3}
          autoResize
          variant="unstyled"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInsightValue(e.target.value)}
          value={insightValue}
        />
      </div>
      <Separator />
      <VStack className="gap-3 p-3">
        <HStack className="gap-2 items-center py-1">
          <Globe size={16} />
          <Input
            placeholder="Source URL"
            variant="unstyled"
            onFocus={(e) => {
              if (url.length === 0) {
                setUrl('https://');
                const element: HTMLInputElement = e.target;
                setTimeout(() => {
                  element.setSelectionRange(8, 8);
                }, 0);
              }
            }}
            onChange={(newUrl) => {
              // Make sure to clean up URL when pasted by user
              const cleanedUrl = newUrl.replace(/^https:\/\/(https?:\/\/)/, '$1');
              setUrl(cleanedUrl);
            }}
            onBlur={(e) => {
              if (e.target.value.length === 0 || validateInput.primitives.url(e.target.value)) {
                setError(null);
              } else {
                setError('Please enter a valid URL');
              }
            }}
            value={url}
          />
        </HStack>
        <HStack className="flex-wrap md:flex-nowrap gap-2">
          <UserCompanySelect
            defaultUser={defaultUser}
            defaultCompany={defaultCompany}
            onChange={({ userID, thirdPartyCompanyID }) => {
              setUserID(userID);
              setThirdPartyCompanyID(thirdPartyCompanyID);
            }}
          />
          <PrioritySelect onChange={setPriority} priority={priority} />
        </HStack>
        <HStack className="gap-2 ml-auto">
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={!canUpsert}
            isLoading={upserting}
            onClick={onUpsert}
            variant="contained">
            {insight ? 'Update' : 'Create'}
          </Button>
        </HStack>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </VStack>
    </Card>
  );
}
